import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

interface FeatureFlag {
  id: number
  key: string
  description: string | null
  enabled: boolean
}

interface Props {
  [key: string]: unknown
  feature_flag: FeatureFlag
}

export default function FeatureFlagEdit() {
  const { feature_flag: flag } = usePage<Props>().props

  const { data, setData, put, processing, errors } = useForm({
    description: flag.description || "",
    enabled: String(flag.enabled),
  })

  const allErrors = Object.values(errors)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    put(`/admin/feature_flags/${flag.id}`)
  }

  return (
    <>
      <Head title={`Edit ${flag.key} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/admin/feature_flags">Feature Flags</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/admin/feature_flags">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Edit Feature Flag</h1>
          </div>
        </div>

        <div className="max-w-lg">
          <form onSubmit={handleSubmit}>
            <div className="rounded-xl border bg-card">
              <div className="p-6">
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label>Key</Label>
                    <Input value={flag.key} disabled className="bg-muted" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">
                      Description <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <textarea
                      id="description"
                      rows={2}
                      className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      value={data.description}
                      onChange={(e) => setData("description", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={data.enabled} onValueChange={(v) => setData("enabled", v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Enabled</SelectItem>
                        <SelectItem value="false">Disabled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />
              <div className="flex justify-end gap-2 p-4">
                <Button variant="ghost" asChild>
                  <a href="/admin/feature_flags">Cancel</a>
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

FeatureFlagEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
